import { useState, useEffect, useCallback, useRef } from "react";
import { TotemHead, TotemSegment, SELF_ANSWER_ID } from "../components/Totem";
import { AnswerInput } from "../components/AnswerInput";
import { ReportModal } from "../components/ReportModal";
import { Toast, ToastType } from "../components/Toast";
import { useSession } from "../hooks/useSession";
import { useTurnstile } from "../hooks/useTurnstile";
import { api } from "../api/client";
import styles from "./Home.module.css";

interface TotemData {
  id: string;
  question: string;
  period_start: string;
  period_end: string;
  total_paid_amount_cents: number;
  total_likes_count: number;
  total_answers_count: number;
  is_eligible_to_like: boolean;
  prize_hint_threshold_cents: number;
  max_answer_length: number;
}

interface Answer {
  answer_id: string;
  content: string;
  created_at: string;
  has_liked?: boolean;
}

interface ToastState {
  message: string;
  type: ToastType;
  isVisible: boolean;
}

export function Home() {
  const { isReady } = useSession();
  const { getToken: getTurnstileToken } = useTurnstile();
  const [totem, setTotem] = useState<TotemData | null>(null);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [currentAnswerIndex, setCurrentAnswerIndex] = useState(0);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [isBrowsing, setIsBrowsing] = useState(false);
  const [isInputCollapsed, setIsInputCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState("");
  const [headerHeight, setHeaderHeight] = useState(280);

  // Report modal state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportAnswerId, setReportAnswerId] = useState<string | null>(null);

  // Toast state
  const [toast, setToast] = useState<ToastState>({
    message: "",
    type: "info",
    isVisible: false,
  });

  // Track liked answers locally
  const [likedAnswers, setLikedAnswers] = useState<Set<string>>(new Set());

  // Track answer input content (to preserve when browsing starts)
  const [answerContent, setAnswerContent] = useState("");

  const isLoadingRef = useRef(false);
  const lastScrollTime = useRef(0);
  const headerRef = useRef<HTMLDivElement>(null);
  const answerInputRef = useRef<HTMLTextAreaElement>(null);
  const cursorPositionRef = useRef<number | null>(null);

  // Check if all answers have been browsed
  const allAnswersBrowsed = totem
    ? answers.length >= totem.total_answers_count &&
      totem.total_answers_count > 0
    : false;
  const hasMoreAnswers = totem
    ? answers.length < totem.total_answers_count
    : true;

  // Show toast helper
  const showToast = useCallback((message: string, type: ToastType = "info") => {
    setToast({ message, type, isVisible: true });
  }, []);

  const hideToast = useCallback(() => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  }, []);

  // Measure header height
  useEffect(() => {
    if (headerRef.current) {
      const updateHeight = () => {
        if (headerRef.current) {
          setHeaderHeight(headerRef.current.offsetHeight);
        }
      };
      updateHeight();
      // Update on resize
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }
  }, [totem, hasAnswered, isInputCollapsed, isBrowsing]);

  // Fetch current totem
  const fetchTotem = useCallback(async () => {
    try {
      const data = await api.get<TotemData>("/totem/current");
      setTotem(data);
      return data;
    } catch (err) {
      if (err instanceof Error && err.message.includes("404")) {
        setError("No active question right now. Check back soon!");
      } else {
        setError("Failed to load. Please refresh.");
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh totem data (for polling)
  const refreshTotem = useCallback(async () => {
    if (!totem) return;
    try {
      const data = await api.get<TotemData>("/totem/current");
      setTotem(data);
    } catch (err) {
      console.error("Failed to refresh totem:", err);
    }
  }, [totem]);

  // Fetch random answer
  const fetchRandomAnswer = useCallback(async () => {
    if (!totem || isLoadingRef.current) return;

    // Don't fetch if we've already seen all answers locally
    if (
      answers.length >= totem.total_answers_count &&
      totem.total_answers_count > 0
    ) {
      return;
    }

    isLoadingRef.current = true;
    setLoadingMore(true);
    try {
      const answer = await api.get<Answer>(`/totem/${totem.id}/random`);
      // Avoid duplicates (shouldn't happen with server-side tracking, but just in case)
      if (!answers.find((a) => a.answer_id === answer.answer_id)) {
        setAnswers((prev) => {
          const newAnswers = [...prev, answer];
          // Move to the new answer
          setCurrentAnswerIndex(newAnswers.length - 1);
          return newAnswers;
        });
        // Track if already liked from server response
        if (answer.has_liked) {
          setLikedAnswers((prev) => new Set(prev).add(answer.answer_id));
        }
      }
    } catch (err: unknown) {
      // Check if this is the "all viewed" response from server
      if (err instanceof Error && err.message.includes("All answers viewed")) {
        // Server confirms all answers seen - refresh totem to get accurate count
        refreshTotem();
      } else {
        console.error("Failed to fetch answer:", err);
      }
    } finally {
      setLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, [totem, answers, refreshTotem]);

  // Start browsing - transition to split view
  const startBrowsing = useCallback(() => {
    // Don't start browsing if there are no answers
    if (!totem || totem.total_answers_count === 0) {
      return;
    }

    if (!isBrowsing) {
      // Save cursor position before transitioning
      if (answerInputRef.current) {
        cursorPositionRef.current = answerInputRef.current.selectionStart;
      }
      setIsBrowsing(true);
      // Only collapse input if user hasn't started typing
      if (!answerContent.trim()) {
        setIsInputCollapsed(true);
      }
      fetchRandomAnswer();
    }
  }, [isBrowsing, fetchRandomAnswer, answerContent, totem]);

  // Focus the input after transitioning to browsing view if user was typing
  useEffect(() => {
    if (isBrowsing && answerContent.trim() && !isInputCollapsed) {
      // Small delay to ensure the new AnswerInput has mounted
      const timer = setTimeout(() => {
        if (answerInputRef.current) {
          answerInputRef.current.focus();
          // Restore cursor position
          const pos = cursorPositionRef.current ?? answerContent.length;
          answerInputRef.current.setSelectionRange(pos, pos);
          cursorPositionRef.current = null;
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isBrowsing]); // Only run when isBrowsing changes

  // Navigate to next answer or fetch new one
  const goToNextAnswer = useCallback(() => {
    if (!isBrowsing) {
      startBrowsing();
      return;
    }
    if (currentAnswerIndex < answers.length - 1) {
      setCurrentAnswerIndex((prev) => prev + 1);
    } else if (hasMoreAnswers) {
      fetchRandomAnswer();
    }
  }, [
    isBrowsing,
    startBrowsing,
    currentAnswerIndex,
    answers.length,
    hasMoreAnswers,
    fetchRandomAnswer,
  ]);

  // Navigate to previous answer
  const goToPrevAnswer = useCallback(() => {
    if (!isBrowsing) return;
    if (currentAnswerIndex > 0) {
      setCurrentAnswerIndex((prev) => prev - 1);
    }
  }, [isBrowsing, currentAnswerIndex]);

  // Submit answer
  const handleSubmitAnswer = async (content: string, isPaid: boolean) => {
    if (!totem) throw new Error("No active totem");

    // Generate mock x402 proof for paid answers (Phase 1 testing)
    let x402Proof: string | undefined;
    if (isPaid) {
      x402Proof = JSON.stringify({
        signature: `mock_sig_${Date.now()}`,
        address: `0x${Math.random().toString(16).slice(2, 42)}`,
        amount: 1,
        timestamp: new Date().toISOString(),
        nonce: `nonce_${Math.random().toString(36).slice(2)}`,
      });
    }

    await api.post("/answer", {
      totem_id: totem.id,
      content,
      is_paid: isPaid,
      x402_proof: x402Proof,
    });

    // Create a local answer object to display immediately
    const selfAnswer: Answer = {
      answer_id: SELF_ANSWER_ID, // Special ID to identify user's own answer
      content: content.trim(),
      created_at: new Date().toISOString(),
    };

    // Add to answers and start browsing with this answer displayed
    setAnswers([selfAnswer]);
    setCurrentAnswerIndex(0);
    setHasAnswered(true);
    setIsBrowsing(true);
    setIsInputCollapsed(true);
    setAnswerContent(""); // Clear the input content
    showToast("Answer submitted!", "success");
    fetchTotem();
  };

  // Like answer
  const handleLike = useCallback(
    async (answerId: string) => {
      // Optimistically update UI
      setLikedAnswers((prev) => new Set(prev).add(answerId));

      try {
        // Get Turnstile token (invisible CAPTCHA)
        const turnstileToken = await getTurnstileToken();

        await api.post("/like", {
          answer_id: answerId,
          turnstile_token: turnstileToken,
        });
        showToast("Liked!", "success");
      } catch (err: unknown) {
        // Revert optimistic update on error
        setLikedAnswers((prev) => {
          const next = new Set(prev);
          next.delete(answerId);
          return next;
        });

        if (err instanceof Error) {
          if (err.message.includes("Already liked")) {
            // Re-add since it was actually liked
            setLikedAnswers((prev) => new Set(prev).add(answerId));
            showToast("Already liked", "info");
          } else if (err.message.includes("your own answer")) {
            showToast("You can't like your own answer", "info");
          } else if (err.message.includes("not yet available")) {
            showToast(
              "Liking opens when prize pool hits $5 or halfway through",
              "info"
            );
          } else if (err.message.includes("Rate limit")) {
            showToast("Too many likes. Please wait.", "error");
          } else if (err.message.includes("Verification")) {
            showToast("Verification failed. Please try again.", "error");
          } else {
            showToast("Failed to like", "error");
          }
        }
      }
    },
    [showToast, getTurnstileToken]
  );

  // Open report modal
  const handleReport = (answerId: string) => {
    setReportAnswerId(answerId);
    setReportModalOpen(true);
  };

  // Submit report
  const handleSubmitReport = async (
    reportType: "spam" | "hate" | "violent" | "other",
    details?: string
  ) => {
    if (!reportAnswerId) return;

    await api.post("/report", {
      answer_id: reportAnswerId,
      report_type: reportType,
      details,
    });
  };

  // Initialize - reset browsing state and fetch totem
  useEffect(() => {
    if (isReady) {
      // Clear server-side seen answers so user can browse fresh on page load
      api.post("/session/reset-browsing").catch(() => {
        // Ignore errors - not critical
      });
      fetchTotem();
    }
  }, [isReady, fetchTotem]);

  // Poll for new answers every minute when browsing
  useEffect(() => {
    if (!isBrowsing || !totem) return;

    const interval = setInterval(() => {
      refreshTotem();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [isBrowsing, totem, refreshTotem]);

  // Countdown timer
  useEffect(() => {
    if (!totem) return;

    const updateCountdown = () => {
      const end = new Date(totem.period_end).getTime();
      const now = Date.now();
      const diff = end - now;

      if (diff <= 0) {
        setCountdown("Closing...");
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(
        `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      );
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [totem]);

  // Like current answer (for keyboard shortcut)
  const likeCurrentAnswer = useCallback(() => {
    if (!isBrowsing || !totem?.is_eligible_to_like) return;
    const currentAnswer = answers[currentAnswerIndex];
    // Don't allow liking self answer or already liked answers
    if (
      currentAnswer &&
      currentAnswer.answer_id !== SELF_ANSWER_ID &&
      !likedAnswers.has(currentAnswer.answer_id)
    ) {
      handleLike(currentAnswer.answer_id);
    }
  }, [
    isBrowsing,
    totem,
    answers,
    currentAnswerIndex,
    likedAnswers,
    handleLike,
  ]);

  // Keyboard navigation
  useEffect(() => {
    if (!totem) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLInputElement
      ) {
        return;
      }

      // Don't handle navigation keys if there are no answers
      if (totem.total_answers_count === 0) {
        return;
      }

      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        goToNextAnswer();
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        goToPrevAnswer();
      } else if (e.key === "l" || e.key === "L") {
        e.preventDefault();
        likeCurrentAnswer();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [totem, goToNextAnswer, goToPrevAnswer, likeCurrentAnswer]);

  // Scroll-based navigation (one answer per scroll)
  // Prevents actual scrolling when in browsing mode
  useEffect(() => {
    if (!totem) return;

    const handleWheel = (e: WheelEvent) => {
      // Only prevent scroll in browsing mode
      if (isBrowsing) {
        e.preventDefault();
      }

      // Don't handle scroll if there are no answers
      if (totem.total_answers_count === 0) {
        return;
      }

      // Debounce - only allow one scroll action per 500ms
      const now = Date.now();
      if (now - lastScrollTime.current < 500) return;

      if (e.deltaY > 30) {
        // Scrolling down
        lastScrollTime.current = now;
        goToNextAnswer();
      } else if (e.deltaY < -30) {
        // Scrolling up
        lastScrollTime.current = now;
        goToPrevAnswer();
      }
    };

    // passive: false allows us to call preventDefault()
    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [totem, isBrowsing, goToNextAnswer, goToPrevAnswer]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading totem...</p>
      </div>
    );
  }

  if (error || !totem) {
    return (
      <div className={styles.error}>
        <p>{error || "Something went wrong"}</p>
        <button
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          Refresh
        </button>
      </div>
    );
  }

  const prizePoolDollars = (totem.total_paid_amount_cents / 100).toFixed(2);
  const currentAnswer = answers[currentAnswerIndex];
  const isAtLastAnswer = currentAnswerIndex === answers.length - 1;
  const canGoNext = !isAtLastAnswer || hasMoreAnswers;

  // Initial intro view - just question and navigation hint
  if (!isBrowsing) {
    return (
      <div className={styles.introContainer}>
        <div className={styles.introContent}>
          <div className={styles.statsBar}>
            <div className={styles.stat}>
              <span className={styles.statValue}>${prizePoolDollars}</span>
              <span className={styles.statLabel}>Prize Pool</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{countdown}</span>
              <span className={styles.statLabel}>Time Left</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>
                {totem.total_answers_count}
              </span>
              <span className={styles.statLabel}>Answers</span>
            </div>
          </div>

          <TotemHead question={totem.question} />

          {/* Answer input for intro view */}
          {!hasAnswered && (
            <div className={styles.introInputSection}>
              {isInputCollapsed ? (
                <button
                  className={styles.expandInputBtn}
                  onClick={() => setIsInputCollapsed(false)}
                >
                  Click here to answer
                </button>
              ) : (
                <div className={styles.inputWrapper}>
                  <button
                    className={styles.collapseBtn}
                    onClick={() => setIsInputCollapsed(true)}
                  >
                    Hide
                  </button>
                  <AnswerInput
                    ref={answerInputRef}
                    onSubmit={handleSubmitAnswer}
                    prizePool={totem.total_paid_amount_cents}
                    prizeHintThreshold={totem.prize_hint_threshold_cents}
                    maxLength={totem.max_answer_length}
                    content={answerContent}
                    onContentChange={setAnswerContent}
                  />
                </div>
              )}
            </div>
          )}

          <div className={styles.introNav}>
            {totem.total_answers_count > 0 ? (
              <>
                <button
                  className={styles.startBrowsingBtn}
                  onClick={startBrowsing}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading..." : "Browse Answers"}
                </button>
                <p className={styles.navHint}>
                  <kbd>↓</kbd>/<kbd>J</kbd> next · <kbd>↑</kbd>/<kbd>K</kbd>{" "}
                  prev · or scroll
                  {totem.is_eligible_to_like && (
                    <>
                      {" "}
                      · <kbd>L</kbd> like
                    </>
                  )}
                </p>
              </>
            ) : (
              <p className={styles.noAnswersYet}>
                No answers yet. Be the first to answer!
              </p>
            )}
          </div>
        </div>

        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={hideToast}
        />
      </div>
    );
  }

  // Browsing view - fixed header + answers
  return (
    <div className={styles.container}>
      {/* Fixed header with stats */}
      <div className={styles.fixedHeader} ref={headerRef}>
        <div className={styles.fixedHeaderInner}>
          <div className={styles.statsBar}>
            <div className={styles.stat}>
              <span className={styles.statValue}>${prizePoolDollars}</span>
              <span className={styles.statLabel}>Prize Pool</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{countdown}</span>
              <span className={styles.statLabel}>Time Left</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>
                {totem.total_answers_count}
              </span>
              <span className={styles.statLabel}>Answers</span>
            </div>
          </div>

          {/* Question - always visible */}
          <TotemHead question={totem.question} />

          {/* Collapsible answer input */}
          {!hasAnswered && (
            <div className={styles.inputSection}>
              {isInputCollapsed ? (
                <button
                  className={styles.expandInputBtn}
                  onClick={() => setIsInputCollapsed(false)}
                >
                  Click here to answer
                </button>
              ) : (
                <>
                  <button
                    className={styles.collapseBtn}
                    onClick={() => setIsInputCollapsed(true)}
                  >
                    Hide
                  </button>
                  <AnswerInput
                    ref={answerInputRef}
                    onSubmit={handleSubmitAnswer}
                    prizePool={totem.total_paid_amount_cents}
                    prizeHintThreshold={totem.prize_hint_threshold_cents}
                    maxLength={totem.max_answer_length}
                    content={answerContent}
                    onContentChange={setAnswerContent}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Spacer to push content below fixed header */}
      <div className={styles.headerSpacer} style={{ height: headerHeight }} />

      {/* Full-screen answer display */}
      <div className={styles.answersViewport}>
        <div className={styles.answerStack}>
          {currentAnswer && (
            <div
              key={currentAnswer.answer_id}
              className={styles.fullScreenAnswer}
            >
              <TotemSegment
                answerId={currentAnswer.answer_id}
                content={currentAnswer.content}
                isNew={currentAnswerIndex === answers.length - 1}
                canLike={totem.is_eligible_to_like}
                hasLiked={likedAnswers.has(currentAnswer.answer_id)}
                onLike={handleLike}
                onReport={handleReport}
              />
            </div>
          )}
        </div>
      </div>

      {/* Fixed answer navigation */}
      <div className={styles.answerNav}>
        <span className={styles.answerCounter}>
          {currentAnswerIndex + 1} / {totem.total_answers_count}
        </span>

        <span className={styles.keyboardHints}>
          <kbd>↓</kbd>/<kbd>J</kbd> next · <kbd>↑</kbd>/<kbd>K</kbd> prev
          {totem.is_eligible_to_like && (
            <>
              {" "}
              · <kbd>L</kbd> like
            </>
          )}
        </span>

        {allAnswersBrowsed && isAtLastAnswer ? (
          <span className={styles.allBrowsedMsg}>All answers viewed ✓</span>
        ) : null}

        <div className={styles.navButtons}>
          <button
            className={styles.navBtn}
            onClick={goToPrevAnswer}
            disabled={currentAnswerIndex === 0}
            aria-label="Previous answer"
          >
            ↑
          </button>
          <button
            className={styles.navBtn}
            onClick={goToNextAnswer}
            disabled={loadingMore || !canGoNext}
            aria-label="Next answer"
            title={!canGoNext ? "All answers viewed" : ""}
          >
            {loadingMore ? "..." : "↓"}
          </button>
        </div>
      </div>

      {/* Report modal */}
      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => {
          setReportModalOpen(false);
          setReportAnswerId(null);
        }}
        onSubmit={handleSubmitReport}
      />

      {/* Toast notifications */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
}
